import format from "date-fns/format"
import { IsMobile } from "../utils/viewport"
import { HotelPagesType } from "../components/element/section/header/types"
import { SearchParamsType } from "../components/calendar/types"

export interface BookingURLType {
  date_format: string
  locale: string
  mobile_url: string
  mobile_url_no_availability: string
  url: string
  url_no_availability: string
  variables: string
}
export interface BookingURLsType {
  name: string
  booking_urls: BookingURLType
}

export interface HotelsOptions {
  [hotelId: string]: string
}

export interface BookingURLsTypes {
  [hotelCode: string]: BookingURLsType
}

export interface HotelOptionsByCityType {
  label: string
  options: { value: string; label: string }[]
}

function RenderTemplate(str: string, obj: any) {
  let parts = str.split(/\$\{(?!\d)[\wæøåÆØÅ]*\}/)
  let args = str.match(/[^{}]+(?=})/g) || []
  let parameters = args.map(
    argument =>
      obj[argument] || (obj[argument] === undefined ? "" : obj[argument])
  )
  return String.raw({ raw: parts }, ...parameters)
}

export const BookingUrl = (
  hotel_code: string,
  urls: BookingURLsType["booking_urls"],
  searchParams: SearchParamsType,
  hasAvailability: boolean,
  rate_code: string | null = null,
  room_code: string | null = null
) => {
  let params = RenderTemplate(urls.variables, {
    hotel_code: hotel_code,
    check_in: format(
      searchParams.check_in,
      urls.date_format.replace(/m/g, "M")
    ),
    check_out: format(
      searchParams.check_out,
      urls.date_format.replace(/m/g, "M")
    ),
    adult_count: searchParams.adult_count,
    child_count: searchParams.child_count,
    total_rooms: 1,
    rate_code: !searchParams.promo_codes.length && rate_code ? rate_code : "",
    promo_code: searchParams.promo_codes.length
      ? searchParams.promo_codes[0]
      : "",
  })
  if (room_code) {
    params += "&room=" + room_code
  }

  let url = IsMobile()
    ? (hasAvailability ? urls.mobile_url : urls.mobile_url_no_availability) +
      "&" +
      params
    : (hasAvailability ? urls.url : urls.url_no_availability) + "&" + params

  // console.log(searchParams)
  // console.log(url)
  window.open(url, "_blank")
  // window.location.href = url
}

const ParseAvailability = (data: any) => {
  if (!data.success) {
    return {}
  }
  data.data.forEach((hotel: any) => {
    hotel.room_rates = {}
    hotel.rates.forEach((room: any) =>
      room.forEach((room_rate: any) => {
        if (!hotel.room_rates.hasOwnProperty(room_rate.room_type_code)) {
          hotel.room_rates[room_rate.room_type_code] = []
        }
        hotel.room_rates[room_rate.room_type_code].push(room_rate)
      })
    )
  })
  return data.data
}

/**
 *
 * Returns Select options sorting by the cities with more hotels, otherwise alphabetically.
 *
 * For instance:
 *
 *  [
 *    {
 *      label: 'Sydney',
 *      options: [
 *        { label: 'Vibe Hotel Sydney', value: "58443" },
 *        { label: 'Vibe Hotel Sydney Darling Harbour', value: "8565" }
 *      ]
 *    },
 *    {
 *     label: 'Mebourne',
 *     options: [
 *        { label: 'Vibe Hotel Melbourne', value: "9969" },
 *      ]
 *    },
 *  ]
 */
export const hotelOptionsByCity = (
  hotelPages: HotelPagesType
): HotelOptionsByCityType[] => {
  let hotelsByCity: { [k: string]: { label: string; value: string }[] } = {
    Sydney: [],
    Melbourne: [],
    Perth: [],
    Hobart: [],
    Canberra: [],
    Adelaide: [],
    "Gold Coast": [],
    Darwin: [],
    Singapore: [],
  }
  const propertyOrder = Object.entries(hotelPages).reduce(
    (acc, [id, hotel]) => {
      if (hotelsByCity[hotel.city]) {
        hotelsByCity[hotel.city].push({ label: hotel.name, value: id })
        acc[id] = hotel.sort
      }
      return acc
    },
    {} as { [k: string]: number }
  )
  return Object.entries(hotelsByCity).map(([city, options]) => {
    return {
      label: city,
      options: options.sort(
        (a, b) => propertyOrder[a.value] - propertyOrder[b.value]
      ),
    }
  })
}

const SearchAvailability = async (
  check_in: Date,
  check_out: Date,
  adult_count: number,
  child_count: number,
  promo_codes: string[],
  rate_codes: string[],
  hotel_codes: string[]
) => {
  if (!hotel_codes) {
    throw new Error('"hotel_codes" must be defined within the page attributes!')
  }
  return fetch(
    `${process.env.SABRE_PROTOCOL}://${process.env.SABRE_HOST}/availability`,
    {
      headers: { "Content-Type": "application/json" },
      method: "POST",
      body: JSON.stringify({
        check_in: format(check_in, "yyyy-MM-dd"),
        check_out: format(check_out, "yyyy-MM-dd"),
        hotel_codes: hotel_codes,
        adult_count: adult_count,
        child_count: child_count,
        area_codes: [],
        promo_codes: promo_codes,
        rate_codes: rate_codes,
        price_range: { min: 0, max: 0 },
        is_mobile: IsMobile(),
        language: "en",
      }),
    }
  )
    .then(response => response.json())
    .then(
      data => ParseAvailability(data),
      error => {
        console.log(error)
      }
    )
}

export const pricePerNight = (total: string, nights: number): string =>
  (parseFloat(total) / nights).toFixed(2)

export default SearchAvailability
